import { useTranslation } from "react-i18next";
import { maybeConfig, type AppEnvironment } from "../config";
import { useKnownServerRole, type ServerRole } from "./ServerRole";

/** The server kind a bar is shown for (servers-and-developers WEB-SRV-BAR-01, -02): none in production. */
export function barFor(environment: AppEnvironment | undefined): Exclude<AppEnvironment, "production"> | undefined {
  return environment === "dev" || environment === "staging" ? environment : undefined;
}

/**
 * The bar for this server: its role from the API wins once a shell has read it (VisionPlatform server-role SR-36), a
 * develop server keeping "staging" when its `config.json` says so; before then, and on an API without the role,
 * `config.json` decides as before.
 */
export function bannerFor(
  role: ServerRole | undefined,
  environment: AppEnvironment | undefined,
): Exclude<AppEnvironment, "production"> | undefined {
  if (role === "production") return undefined;
  if (role === "develop") return environment === "staging" ? "staging" : "dev";
  return barFor(environment);
}

/** The bar shown now: the role when known, otherwise `config.json`. */
export function useBanner(): Exclude<AppEnvironment, "production"> | undefined {
  return bannerFor(useKnownServerRole(), maybeConfig()?.environment);
}

/**
 * The development bar (WEB-SRV-BAR-01, VisionPlatform SRV-26): on a server that is not production, every screen of
 * the customer app and the console says so above the top bar, in a colour no other banner uses, so no screenshot
 * from `dev` passes as production. It comes from the server's role and `config.json`, never from a host name (SRV-08).
 */
export function EnvironmentBar() {
  const { t } = useTranslation();
  const kind = useBanner();
  if (!kind) return null;
  return (
    <div
      role="note"
      data-environment={kind}
      title={t("environment.bar.title")}
      className="bg-violet-800 px-4 py-0.5 text-center text-xs text-white"
    >
      {t(`environment.bar.${kind}`)}
    </div>
  );
}

/** "Development" or "Staging" for the user menu (WEB-SRV-BAR-03); nothing in production. */
export function useServerName(): string | undefined {
  const { t } = useTranslation();
  const kind = useBanner();
  return kind ? t(`environment.name.${kind}`) : undefined;
}
